import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { formatInTimeZone } from "date-fns-tz";
import { parseISO, format } from "date-fns";

const PRIMARY = [60, 95, 151]; // Azul primário do sistema (#3c5f97)
const GRAY_LIGHT = [245, 247, 250];
const GRAY_BORDER = [196, 194, 194];
const GRAY_TEXT = [107, 114, 128];
const GRAY_DARK = [31, 41, 55];

function fmtCurrency(val) {
	const num = Number(val ?? 0);
	return num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtDate(dateStr) {
	if (!dateStr) return "—";
	try {
		return format(parseISO(dateStr), "dd/MM/yyyy");
	} catch {
		return dateStr;
	}
}

function fmtDateTime(dateStr) {
	if (!dateStr) return "—";
	try {
		return format(parseISO(dateStr), "dd/MM/yyyy HH:mm");
	} catch {
		return dateStr;
	}
}

export function gerarPdfVendasCompacto(vendas = [], paginaAtual = 1, filtrosAplicados = {}, empresa = "SZ Sistemas") {
	if (vendas.length === 0) return;

	const doc = new jsPDF("p", "mm", "a4");
	const pageWidth = doc.internal.pageSize.getWidth();
	const pageHeight = doc.internal.pageSize.getHeight();
	const margin = 12;
	const contentWidth = pageWidth - (margin * 2);

	// Processa filtros de forma genérica (suporta array ou objeto)
	let linhasFiltro = [];
	if (Array.isArray(filtrosAplicados)) {
		filtrosAplicados.forEach(f => {
			if (f && typeof f === 'object') {
				const label = f.label || 'Filtro';
				const value = f.value || '—';
				linhasFiltro.push(`${label}: ${value}`);
			}
		});
	} else if (typeof filtrosAplicados === 'object' && filtrosAplicados !== null) {
		Object.entries(filtrosAplicados).forEach(([key, filterObj]) => {
			if (filterObj && typeof filterObj === 'object') {
				const label = filterObj.label || key;
				const value = filterObj.value || '—';
				linhasFiltro.push(`${label}: ${value}`);
			}
		});
	}

	// ─── CABEÇALHO LADO DIREITO (FILTROS E PÁGINA) ───────────────────────────
	let yFiltro = 15;
	doc.setFont("helvetica", "bold");
	doc.setFontSize(8.5);
	doc.setTextColor(...PRIMARY);
	doc.text("Filtros Aplicados", pageWidth - margin, yFiltro, { align: "right" });
	yFiltro += 4.5;

	doc.setFont("helvetica", "normal");
	doc.setFontSize(7.5);
	doc.setTextColor(...GRAY_TEXT);
	
	// Exibe a página do sistema que foi impressa
	doc.text(`Página do Sistema: ${paginaAtual}`, pageWidth - margin, yFiltro, { align: "right" });
	yFiltro += 3.8;

	linhasFiltro.forEach(linha => {
		doc.text(String(linha), pageWidth - margin, yFiltro, { align: "right" });
		yFiltro += 3.8;
	});

	// ─── CABEÇALHO LADO ESQUERDO (TÍTULO E SUBTÍTULO) ────────────────────────
	let yTitulo = 16;
	doc.setFont("helvetica", "bold");
	doc.setFontSize(14);
	doc.setTextColor(...PRIMARY);
	doc.text("Relatório de Vendas Compacto", margin, yTitulo);
	yTitulo += 5.5;

	doc.setFont("helvetica", "normal");
	doc.setFontSize(8);
	doc.setTextColor(...GRAY_TEXT);
	doc.text(`Exibindo vendas da página ${paginaAtual} do sistema.`, margin, yTitulo);
	yTitulo += 4.5;

	// Risco azul do título principal
	doc.setDrawColor(...PRIMARY);
	doc.setLineWidth(0.4);
	doc.line(margin, yTitulo, margin + 70, yTitulo);
	yTitulo += 4;

	// O y inicial das vendas é o maior entre os dois lados do cabeçalho
	let y = Math.max(yTitulo, yFiltro) + 6;

	// Itera sobre as vendas
	vendas.forEach((venda, index) => {
		// Estima a altura necessária para esta venda (cabeçalho + itens + parcelas + margem)
		const numItens = Array.isArray(venda.itens) ? venda.itens.length : 0;
		const numParcelas = Array.isArray(venda.parcelas) ? venda.parcelas.length : 0;
		
		// Altura aproximada em mm: cabeçalho(8) + itens(tabela: header(6) + itens*5) + parcelas(6) + espaçamento(10)
		const estimatedHeight = 8 + 6 + (numItens * 5) + 6 + 10;

		// Se ultrapassar a folha, adiciona nova página e reinicia o y
		if (y + estimatedHeight > pageHeight - 12) {
			doc.addPage();
			y = 14;
		}

		// Faixa divisória / Cabeçalho da Venda
		doc.setFillColor(...GRAY_LIGHT);
		doc.rect(margin, y, contentWidth, 7, "F");

		doc.setFont("helvetica", "bold");
		doc.setFontSize(8.5);
		doc.setTextColor(...PRIMARY);
		doc.text(`Venda #${venda.id_venda}`, margin + 2, y + 4.8);
		
		doc.setFont("helvetica", "normal");
		doc.setFontSize(8);
		doc.setTextColor(...GRAY_TEXT);
		const dataVendaStr = fmtDateTime(venda.datavenda);
		doc.text(`Data: ${dataVendaStr}  |  Vendedor: ${venda.nome_funcionario || "—"}`, margin + 28, y + 4.8);

		const totalVendaStr = fmtCurrency(venda.valorvenda);
		doc.setFont("helvetica", "bold");
		doc.text(`Total: ${totalVendaStr}`, pageWidth - margin - 2, y + 4.8, { align: "right" });

		y += 7.5;

		// Dados Detalhados do Cliente (Dispostos em Grid sutil de 4 colunas sem bordas para melhor visibilidade)
		autoTable(doc, {
			startY: y,
			body: [
				[
					{ content: "Cliente:", styles: { fontStyle: "bold", textColor: PRIMARY } },
					venda.nome || "Consumidor Final",
					{ content: "CPF/CNPJ:", styles: { fontStyle: "bold", textColor: PRIMARY } },
					venda.cpf || "—"
				],
				[
					{ content: "Telefone:", styles: { fontStyle: "bold", textColor: PRIMARY } },
					venda.telefone_1 || "—",
					{ content: "Cidade/UF:", styles: { fontStyle: "bold", textColor: PRIMARY } },
					`${venda.cidade || "—"}/${venda.uf || "—"}`
				]
			],
			margin: { left: margin, right: margin },
			theme: "plain",
			styles: {
				fontSize: 7.5,
				cellPadding: 0.8,
				textColor: GRAY_DARK
			},
			columnStyles: {
				0: { cellWidth: 15, fontStyle: "bold" },
				1: { cellWidth: "auto" },
				2: { cellWidth: 20, fontStyle: "bold" },
				3: { cellWidth: 50 }
			},
			didDrawPage: (data) => {
				y = data.cursor.y;
			}
		});

		y += 3.5;

		// Subtabela de Itens (usando AutoTable de forma bem compacta)
		const itensData = [];
		const rowTypes = [];
		let seq = 1;
		(venda.itens || []).forEach((item) => {
			// Adiciona o item principal
			const nomeItem = item.nome ? (item.descricao && item.descricao !== item.nome ? `${item.nome} - ${item.descricao}` : item.nome) : (item.descricao || "Item sem descrição");
			
			const temComplementos = Array.isArray(item.complementos) && item.complementos.length > 0;
			const isTemplateGrade = temComplementos && Number(item.valor_unit || 0) === 0;

			if (isTemplateGrade) {
				// Se for o template de grade, mescla o restante da linha (colSpan: 4) para tirar as divisórias verticais das colunas
				itensData.push([
					String(seq),
					{ content: nomeItem, colSpan: 4 }
				]);
				rowTypes.push("item-header");
			} else {
				itensData.push([
					String(seq),
					nomeItem,
					String(item.qtde ?? 1),
					fmtCurrency(item.valor_unit),
					fmtCurrency(item.valor)
				]);
				rowTypes.push("simple");
			}
			seq++;

			// Adiciona cada complemento em sua própria linha dedicada
			if (Array.isArray(item.complementos)) {
				item.complementos.forEach((comp) => {
					itensData.push([
						"", // Sequencial vazio para complementos
						`        ${comp.nome || "Complemento"}`,
						String(comp.qtde ?? 1),
						fmtCurrency(comp.valorunit),
						fmtCurrency(comp.valor)
					]);
					rowTypes.push("complement");
				});
			}
		});

		autoTable(doc, {
			startY: y,
			head: [["#", "Produto / Complemento", "Qtde", "Valor Unit.", "Total"]],
			body: itensData,
			margin: { left: margin, right: margin },
			theme: "grid",
			styles: {
				fontSize: 7.5,
				cellPadding: 1.5,
				lineColor: GRAY_BORDER,
				lineWidth: 0.1
			},
			headStyles: {
				fillColor: PRIMARY,
				textColor: 255,
				fontStyle: "bold"
			},
			columnStyles: {
				0: { halign: "center", cellWidth: 8 },
				1: { cellWidth: "auto" },
				2: { halign: "center", cellWidth: 14 },
				3: { halign: "right", cellWidth: 28 },
				4: { halign: "right", cellWidth: 28 }
			},
			didParseCell: (data) => {
				if (data.section !== "body") return;
				const type = rowTypes[data.row.index];
				if (type === "item-header") {
					data.cell.styles.fillColor = [235, 240, 250]; // Fundo azul suave
					data.cell.styles.fontStyle = "bold";
					data.cell.styles.textColor = PRIMARY;
				} else if (type === "complement") {
					data.cell.styles.fillColor = [249, 250, 252]; // Fundo cinza suave das variações
					data.cell.styles.textColor = [75, 85, 99]; // Cinza original dos adicionais
				} else {
					// Alterna o fundo entre branco e cinza bem claro para produtos simples
					const itemIdx = itensData.slice(0, data.row.index).filter((_, i) => rowTypes[i] === "simple").length;
					data.cell.styles.fillColor = itemIdx % 2 === 0 ? [255, 255, 255] : [245, 247, 250];
				}
			},
			didDrawPage: (data) => {
				y = data.cursor.y;
			}
		});

		y += 2.5;

		// Tabela de Financeiro/Parcelas (Estruturada e com Rodapé totalizador muito destacado)
		const parcelas = venda.parcelas || [];
		if (parcelas.length > 0) {
			const totalParcelas = parcelas.reduce((s, p) => s + Number(p.valor || 0), 0);
			const bodyParcela = parcelas.map((p) => {
				const isPending = p.ativo === "SIM" || p.ativo === true || p.ativo === 1;
				return [
					`${p.parcela ?? "—"}/${p.parcela_total ?? "—"}`,
					fmtDate(p.data),
					p.forma || "—",
					isPending ? "Pendente" : "Pago",
					fmtCurrency(p.valor)
				];
			});

			autoTable(doc, {
				startY: y,
				head: [["Parc.", "Vencimento", "Forma Pgto", "Status", "Valor"]],
				body: bodyParcela,
				foot: [[{ content: `Total: ${fmtCurrency(totalParcelas)}`, colSpan: 5 }]],
				margin: { left: margin, right: margin },
				theme: "grid",
				styles: {
					fontSize: 7.2,
					cellPadding: 1.5,
					lineColor: GRAY_BORDER,
					lineWidth: 0.1
				},
				headStyles: {
					fillColor: PRIMARY,
					textColor: 255,
					fontStyle: "bold"
				},
				footStyles: {
					fillColor: [235, 240, 250], // Fundo azul suave (destaque igual ao do template de grade)
					textColor: PRIMARY, // Texto no azul primário do sistema
					fontStyle: "bold",
					halign: "right" // Alinha à direita
				},
				columnStyles: {
					0: { halign: "center", cellWidth: 15 },
					1: { halign: "center", cellWidth: 22 },
					2: { cellWidth: "auto" },
					3: { halign: "center", cellWidth: 20 },
					4: { halign: "right", cellWidth: 28 }
				},
				didParseCell: (data) => {
					if (data.column.index === 3 && data.section === "body") {
						const isPending = data.cell.raw === "Pendente";
						data.cell.styles.textColor = isPending ? [185, 28, 28] : [22, 101, 52];
						data.cell.styles.fontStyle = "bold";
					}
				},
				didDrawPage: (data) => {
					y = data.cursor.y;
				}
			});
		} else {
			y += 1.5;
		}

		// Espaço entre as vendas
		y += 4;
	});

	// Rodapé Geral
	const totalPaginas = doc.internal.getNumberOfPages();
	for (let i = 1; i <= totalPaginas; i++) {
		doc.setPage(i);
		doc.setFontSize(7);
		doc.setTextColor(...GRAY_TEXT);
		doc.text(`Página ${i} de ${totalPaginas}`, pageWidth - margin, pageHeight - 6, { align: "right" });
		doc.text("Portal SZ Sistemas - Relatório de Vendas Compactado", margin, pageHeight - 6);
	}

	const blob = doc.output("blob");
	const url = URL.createObjectURL(blob);
	window.open(url, "_blank");
}
